import { CardGrid } from '@liveauctioneers/hammer-ui-core/card/cardGrid';
import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { cardFactory } from '@liveauctioneers/hammer-ui-core/card/factory';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

const DEFALUT_CAROUSEL_SKELETON_LOADER_ROWS = 1;
const CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_XS = 2;
const CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_SM = 4;
const CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_MD = 5;
const CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_LG = 5;

type CarouselsSkeletonLoaderProps = {
    className?: string;
    rows: number;
};

const ItemCard = cardFactory({
    components: [
        createImage({
            ImageComponent: () => (
                <div className="overflow-hidden rounded-md border border-border-tertiary">
                    <div
                        className="aspect-square animate-pulse bg-background-secondary"
                        data-testid="skeleton-card"
                    />
                </div>
            ),
        }),
        { spacerPx: 12 },
        createInfo({
            skeletonWidth: 100,
            useText: null,
        }),
        { spacerPx: 8 },
        createInfo({
            skeletonWidth: 200,
            useText: null,
        }),
    ],
    name: 'ItemCard',
});

const CarouselsSkeletonLoader = ({
    className,
    rows = DEFALUT_CAROUSEL_SKELETON_LOADER_ROWS,
}: CarouselsSkeletonLoaderProps) => {
    const {
        breakpoints: { lgMinValue, mdMinValue, smMinValue },
    } = useTheme();

    const isXs = useIsNarrowerThan(smMinValue);
    const isSm = useIsNarrowerThan(mdMinValue);
    const isMd = useIsNarrowerThan(lgMinValue);

    const skeletonCount = useMemo(() => {
        if (isXs) {
            return CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_XS;
        }
        if (isSm) {
            return CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_SM;
        }
        if (isMd) {
            return CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_MD;
        }
        return CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_LG;
    }, [isXs, isSm, isMd]);

    return (
        <CardGrid
            card={ItemCard}
            cardData={{}}
            cards={[]}
            className={`carousel-loader mt-xs ${className}`}
            colsByBreakpoint={{
                lg: CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_LG,
                md: CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_MD,
                sm: CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_SM,
                xs: CAROUSEL_SKELETON_LOADER_CARDS_COUNT_ON_XS,
            }}
            data-testid="carousel-loader"
            forceSkeleton
            gapsByBreakpoint={{
                lg: 'md',
                md: 'md',
                sm: 'sm',
                xl: 'md',
                xs: 'sm',
            }}
            skeletonCardCount={skeletonCount * rows}
        />
    );
};

export default CarouselsSkeletonLoader;
